import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import moment from "moment";
import sessionHandling from "services/utils/notificationUtils";
import { designWeights } from "services/constants/designTokens";
import { EmptyState } from "components/CustomComponents/EmptyState";
import { checkViewerPermission } from "services/utils/HelperFunction";
import { dashboard } from "./dashboardStyles";

// Upcoming Holidays card. Reads the same `companyleave` endpoint the leave
// calendar (MyZone/Calendar) and HR holiday admin screen use — each row is
// { id, date: "DD-MM-YYYY", leave_type: "<name>" } — then keeps only holidays
// from today onward, soonest first.
export const UpcomingHolidaysSection = () => {
  const { domain, token, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  const history = useHistory();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);

  // This card links straight to My Zone > Calendar, whose own sidebar row
  // is now gated on "Calendar" viewer access (see Sidebar/index.js's
  // MY_WORKSPACE_VIEWER_GATED_MODULES) -- the Home preview should agree
  // with that instead of showing a shortcut to a screen the user can't see.
  const hasAccess = checkViewerPermission(
    userScreenAccess?.module_details,
    "Calendar",
    "Calendar",
  );

  useEffect(() => {
    let cancelled = false;
    const fetchHolidays = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${domain}companyleave/`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
        });
        const res = await response.json();
        if (response.ok) {
          const today = moment().startOf("day");
          const upcoming = (Array.isArray(res) ? res : [])
            .map((h) => ({ ...h, _m: moment(h.date, "DD-MM-YYYY") }))
            .filter((h) => h._m.isValid() && !h._m.isBefore(today))
            .sort((a, b) => a._m.valueOf() - b._m.valueOf());
          if (!cancelled) setHolidays(upcoming);
        } else if (response.status === 409) {
          sessionHandling();
        }
      } catch (error) {
        console.error("[UpcomingHolidaysSection] operation failed:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (hasAccess) fetchHolidays();
    else setLoading(false);
    return () => {
      cancelled = true;
    };
  }, [domain, token, hasAccess]);

  if (!hasAccess) return null;

  const next = holidays.slice(0, 3);

  return (
    <Box sx={dashboard.cardSx}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <Typography fontWeight={designWeights.heading} sx={{ color: dashboard.colors.text }}>
          Upcoming Holidays
        </Typography>
        <Typography
          sx={{ ...dashboard.linkSx, color: dashboard.colors.blue }}
          onClick={() => history.push("/admin/myzone/calendar")}
        >
          View Calendar →
        </Typography>
      </Stack>
      {loading ? (
        <CircularProgress size={22} />
      ) : next.length === 0 ? (
        <EmptyState subtitle="No upcoming holidays." size={96} sx={{ py: 2 }} />
      ) : (
        <Box>
          {next.map((h, idx) => (
            <Stack
              key={h.id ?? idx}
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{
                py: 1.1,
                borderBottom:
                  idx !== next.length - 1 ? "1px solid #F0F1F4" : "none",
              }}
            >
              <Box
                sx={{
                  width: 46,
                  minWidth: 46,
                  textAlign: "center",
                  borderRadius: 2,
                  py: 0.5,
                  bgcolor: "#FEF3F2",
                }}
              >
                <Typography fontWeight={designWeights.heading} sx={{ color: dashboard.colors.text, lineHeight: 1 }}>
                  {h._m.format("DD")}
                </Typography>
                <Typography variant="caption" sx={{ color: "#F04438", fontWeight: designWeights.medium }}>
                  {h._m.format("MMM").toUpperCase()}
                </Typography>
              </Box>
              <Box>
                <Typography fontWeight={designWeights.medium} sx={{ color: dashboard.colors.text }}>
                  {h.leave_type || "Holiday"}
                </Typography>
                <Typography variant="caption" sx={{ color: dashboard.colors.subText }}>
                  {h._m.format("dddd, DD MMMM YYYY")}
                </Typography>
              </Box>
            </Stack>
          ))}
          <Typography
            sx={{ ...dashboard.linkSx, color: dashboard.colors.blue, mt: 1.5, textAlign: "center" }}
            onClick={() => history.push("/admin/myzone/calendar")}
          >
            View All Holidays →
          </Typography>
        </Box>
      )}
    </Box>
  );
};
